import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, ChevronDown, ChevronUp } from 'lucide-react';
import ProfilePicture from '@/components/ProfilePicture';

const ContributorsList = ({ 
  contributors, 
  settings, 
  expandedContributor,
  setExpandedContributor,
  onAdjustPoints,
  onRemove 
}) => {
  return (
    <div className="grid gap-4">
      {contributors.map(contributor => (
        <Card 
          key={contributor.id} 
          className={`border ${contributor.is_active ? 'border-gray-700' : 'border-red-500/30 opacity-60'}`}
        >
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                <ProfilePicture 
                  src={contributor.user?.profile_picture} 
                  alt={contributor.user?.full_name} 
                  size="lg"
                />
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-semibold text-base sm:text-lg truncate">{contributor.user?.full_name}</h3>
                    {!contributor.is_active && (
                      <Badge variant="destructive">Inactive</Badge>
                    )}
                    {contributor.is_eligible && (
                      <Badge className="bg-green-600">Eligible</Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 truncate">{contributor.user?.email}</p>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-1 text-sm">
                    <span className="text-cyan-400 font-medium">
                      {contributor.current_month_points || 0} pts this month
                    </span>
                    {!contributor.is_eligible && (
                      <span className="text-gray-500">
                        ({(contributor.eligibility_threshold || settings.eligibility_threshold) - (contributor.current_month_points || 0)} pts to eligibility)
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-2 flex-shrink-0 ml-auto sm:ml-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onAdjustPoints(contributor)}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">Adjust Points</span>
                  <span className="sm:hidden">Adjust</span>
                </Button>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setExpandedContributor(
                    expandedContributor === contributor.id ? null : contributor.id
                  )}
                >
                  {expandedContributor === contributor.id ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </Button>
                
                {contributor.is_active && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => onRemove(contributor.user_id)}
                  >
                    Remove
                  </Button>
                )}
              </div>
            </div>
            
            {/* Expanded Details */}
            {expandedContributor === contributor.id && (
              <div className="mt-4 pt-4 border-t border-gray-700">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-400 mb-2">Mailing Address</h4>
                    {contributor.mailing_address ? (
                      <div className="text-sm">
                        <p>{contributor.mailing_address}</p>
                        <p>{contributor.mailing_city}, {contributor.mailing_state} {contributor.mailing_zip}</p>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No address on file</p>
                    )}
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-400 mb-2">Recent Activity</h4>
                    {contributor.points_history?.slice(0, 3).map((activity, i) => (
                      <div key={i} className="text-sm flex justify-between">
                        <span>{activity.activity_type}</span>
                        <span className="text-cyan-400">+{activity.points}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default ContributorsList;
